import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { setPreferredTimeSlots, AppointmentTimePreference } from '../redux/slices/appointmentSlice';
import { StepCard } from './StepCard';
import { PreferredSlotCard, PreferredSlot } from './appointment/PreferredSlotCard';
import { formatDateToISO, format24HourTo12Hour, format12HourTo24Hour, isPracticeClosed } from '../utils/helpers';
import { PhoneIcon } from 'lucide-react';

interface PreferredTimeSlotsProps {
  onValidationChange: (isValid: boolean) => void;
}

const PreferredTimeSlots: React.FC<PreferredTimeSlotsProps> = ({ onValidationChange }) => {
  const dispatch = useDispatch();
  const { practice } = useSelector((s: RootState) => s.auth);
  const { preferredTimeSlots } = useSelector((s: RootState) => s.appointment);

  // Initialize state from Redux
  const [preferredSlot, setPreferredSlot] = useState<PreferredSlot>(() => {
    const saved = preferredTimeSlots.find(s => s.priority === 1);
    const date = saved?.preferred_date ? new Date(saved.preferred_date + 'T00:00:00') : undefined;
    return {
      date,
      startTime: saved?.start_time ? format12HourTo24Hour(saved.start_time.split(' ').slice(1).join(' ')) : "",
      endTime: saved?.end_time ? format12HourTo24Hour(saved.end_time.split(' ').slice(1).join(' ')) : "",
      isAnyTime: saved?.any_time || false,
    };
  });

  const [alternateSlot, setAlternateSlot] = useState<PreferredSlot>(() => {
    const saved = preferredTimeSlots.find(s => s.priority === 2);
    const date = saved?.preferred_date ? new Date(saved.preferred_date + 'T00:00:00') : undefined;
    return {
      date,
      startTime: saved?.start_time ? format12HourTo24Hour(saved.start_time.split(' ').slice(1).join(' ')) : "",
      endTime: saved?.end_time ? format12HourTo24Hour(saved.end_time.split(' ').slice(1).join(' ')) : "",
      isAnyTime: saved?.any_time || false,
    };
  });

  const isDateClosed = (date?: Date) => isPracticeClosed(practice?.working_hours, date);

  const isPreferredSlotComplete = () => {
    if (!preferredSlot.date || isDateClosed(preferredSlot.date)) return false;
    if (preferredSlot.isAnyTime) return true;
    return !!preferredSlot.startTime && !!preferredSlot.endTime;
  };

  useEffect(() => {
    const slots: AppointmentTimePreference[] = [];

    if (preferredSlot.date && !isDateClosed(preferredSlot.date)) {
      const dateStr = formatDateToISO(preferredSlot.date);
      slots.push({
        preferred_date: dateStr,
        start_time: preferredSlot.isAnyTime ? null : (preferredSlot.startTime ? `${dateStr} ${format24HourTo12Hour(preferredSlot.startTime)}` : null),
        end_time: preferredSlot.isAnyTime ? null : (preferredSlot.endTime ? `${dateStr} ${format24HourTo12Hour(preferredSlot.endTime)}` : null),
        priority: 1,
        any_time: preferredSlot.isAnyTime,
      });
    }

    if (alternateSlot.date && !isDateClosed(alternateSlot.date)) {
      const dateStr = formatDateToISO(alternateSlot.date);

      // Fallback Logic: Treat as "Any Time" if times are incomplete (e.g. Date only, or Date + Start only)
      const hasSpecificTimes = alternateSlot.startTime && alternateSlot.endTime;
      const effectiveAnyTime = alternateSlot.isAnyTime || !hasSpecificTimes;

      slots.push({
        preferred_date: dateStr,
        start_time: effectiveAnyTime ? null : `${dateStr} ${format24HourTo12Hour(alternateSlot.startTime)}`,
        end_time: effectiveAnyTime ? null : `${dateStr} ${format24HourTo12Hour(alternateSlot.endTime)}`,
        priority: 2,
        any_time: effectiveAnyTime,
      });
    }

    dispatch(setPreferredTimeSlots(slots));
    onValidationChange(isPreferredSlotComplete());
  }, [preferredSlot, alternateSlot, dispatch, onValidationChange, practice]);

  return (
    <StepCard className='overflow-y-auto h-[460px]'>
      <div className="space-y-3">
        <PreferredSlotCard
          slot={preferredSlot}
          onSlotChange={setPreferredSlot}
          priority={1}
          label="Preferred Time Slot"
          badgeType="primary"
          statusLabel="Required"
          statusBadgeStyles="badge-error badge-outline"
        />
        <PreferredSlotCard
          slot={alternateSlot}
          onSlotChange={setAlternateSlot}
          priority={2}
          label="Alternate Time Slot"
          badgeType="outline"
          statusLabel="Optional"
          statusBadgeStyles="badge-secondary badge-outline"
          disabled={!isPreferredSlotComplete()}
        />
        <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 text-xs sm:text-sm text-blue-900 rounded-lg px-4 py-2">
          <PhoneIcon className="h-5 w-5 flex-shrink-0 mt-0.5 text-blue-600" />
          <div className="flex-1">
            <p className="font-medium mb-1">We'll Call You to Confirm</p>
            <p className="text-blue-800">Your appointment time is not guaranteed. We will call you to confirm availability.</p>
          </div>
        </div>
      </div>
    </StepCard>
  );
};

export default PreferredTimeSlots;
